import { useCallback, useRef } from 'react';

/**
 * Float noise between two independently laid-out rects is orders of magnitude
 * below this; anything above it is real overflow the browser had to clip.
 */
const SUB_PIXEL_EPSILON = 0.05;

/** Fractional content-box width — `clientWidth` rounds, `getBoundingClientRect` does not. */
function contentBoxWidth(node: HTMLElement): number {
    const style = getComputedStyle(node);
    const horizontalInset =
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0) +
        (parseFloat(style.borderLeftWidth) || 0) +
        (parseFloat(style.borderRightWidth) || 0);
    return node.getBoundingClientRect().width - horizontalInset;
}

function isLineClamped(node: HTMLElement): boolean {
    const clamp = getComputedStyle(node).webkitLineClamp;
    return Boolean(clamp) && clamp !== 'none';
}

/**
 * Measures, on demand, whether an element's text is actually clipped by CSS
 * truncation (`truncate` / `text-overflow: ellipsis`) — so callers can offer a
 * reveal-the-full-value affordance ONLY when something is genuinely hidden.
 *
 * Deliberately imperative rather than a piece of reactive state. Whether the
 * ellipsis engages depends on column width and on the loaded font, and neither
 * a web-font swap nor a parent's re-layout changes the element's own box, so a
 * `ResizeObserver` would not fire and a mount-time reading would go stale.
 * Measuring at the moment of interaction is always current.
 *
 * @example
 * const { ref, measure } = useIsTruncated<HTMLSpanElement>();
 * <span ref={ref} className="truncate">{email}</span>
 * // ...later, when deciding whether to open an overlay:
 * if (measure()) open();
 */
export function useIsTruncated<T extends HTMLElement>() {
    const ref = useRef<T | null>(null);

    const measure = useCallback((): boolean => {
        const node = ref.current;
        if (!node) return false;

        // Fast path: the whole-pixel boxes disagree by more than the 1px that
        // sub-pixel layout rounding can invent, so the text is definitely clipped.
        if (node.scrollWidth > node.clientWidth + 1) return true;

        // Multi-line clamp (`line-clamp-N`) wraps instead of overflowing sideways,
        // so the hidden lines only show up as vertical overflow. Scoped to clamped
        // nodes: a single-line `truncate` box can overhang by a pixel of descender.
        if (isLineClamped(node)) return node.scrollHeight > node.clientHeight + 1;

        // Borderline, and `scrollWidth`/`clientWidth` are too coarse to judge
        // it: both are rounded to whole pixels, so text that overflows by a
        // fraction — already enough for the browser to drop a character and
        // paint an ellipsis — rounds to "fits" and the value silently refuses
        // to reveal itself. Two same-length addresses can land on either side
        // of that rounding, which is why only *some* visibly-ellipsised cells
        // used to open on hover or click.
        //
        // So measure the text's own box instead. Range rects come from layout,
        // and `text-overflow` only changes painting, so this is the full
        // untruncated width even while the ellipsis is on screen.
        const range = document.createRange();
        // jsdom implements no layout and omits this entirely; treating the
        // borderline case as "fits" there matches the coarse check above.
        if (typeof range.getBoundingClientRect !== 'function') return false;
        range.selectNodeContents(node);
        const textWidth = range.getBoundingClientRect().width;
        // A detached or display:none node (and jsdom, which has no layout)
        // reports 0 — there is nothing measurable to call truncated.
        if (textWidth === 0) return false;

        return textWidth > contentBoxWidth(node) + SUB_PIXEL_EPSILON;
    }, []);

    return { ref, measure };
}
